import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import ModalContext from 'contexts/ModalContext'
import TextButton from 'LuxKit/Button/TextButton'
import SolidSparklesIcon from 'LuxKit/Icons/solid/SolidSparklesIcon'
import StatusLabel from 'LuxKit/Label/StatusLabel'
import HeadingTextBlock from 'LuxKit/TextBlocks/HeadingTextBlock'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import React, { useCallback, useContext } from 'react'
import Sheet from 'components/utils/Sheet'
import { OptimizelyFeatureFlags } from 'constants/optimizely'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { useAppSelector } from 'hooks/reduxHooks'
import { FormattedMessage } from 'react-intl'

interface Props {
  loggedIn: boolean
}
function TripLandingPageHeader(props: Props) {
  const showModal = useContext(ModalContext)
  const isTripChatPageEnabled = useOptimizelyExperiment(OptimizelyFeatureFlags.trpTripChatPage)
  const deviceType = useAppSelector(state => state.system.device.deviceType)
  const isDesktop = deviceType !== 'mobile' && deviceType !== 'tablet'
  const showAITripPlanner = isTripChatPageEnabled && isDesktop

  const onLoginClick = useCallback(() => {
    showModal(<AccountAccessModal />)
  }, [showModal])

  const contentWithFeatureFlag = (
    <Group direction="vertical" gap={16} desktopDirection="horizontal" verticalAlign="center" desktopHorizontalAlign="space-between" desktopGap={24}>
      <Group direction="vertical" gap={16}>
        <Group direction="vertical" gap={8}>
          <StatusLabel
            variant="confirmation"
            startIcon={<SolidSparklesIcon />}
          >
            <FormattedMessage
              id="AN+Ox9JWkN"
              defaultMessage="AI Trip Planner"
              description="Label for AI Trip Planner feature"
            />
          </StatusLabel>
          <HeadingTextBlock variant="heading1">
            <Heading variant="heading1">
              <FormattedMessage
                id="v5uvwpboew"
                defaultMessage="The <i>easiest</i> way"
                description="Heading text for easiest way to plan"
                values={{
                  i: (chunks: React.ReactNode) => <i key="italic">{chunks}</i>,
                }}
              />
            </Heading>
            <Heading variant="heading1">
              <FormattedMessage
                id="bz1tHsQ/7B"
                defaultMessage="to plan a holiday, ever"
                description="Heading text for to plan a holiday ever"
              />
            </Heading>
          </HeadingTextBlock>
          <BodyText variant="large">
            <FormattedMessage
              id="MlXmgTxWfl"
              defaultMessage="We combine expert travel knowledge with powerful AI{br} to make trip planning effortless, smart and fun."
              description="Hero body copy describing AI-powered trip planning"
              values={{
                br: <CSSBreakpoint as="br" min="tablet" />,
              }}
            />
          </BodyText>
        </Group>
        <Group direction="vertical" gap={16} verticalAlign="center" horizontalAlign="start">
          <TextButton kind="primary" size="large" startIcon={<SolidSparklesIcon />} shape="round" to="/trip-planner/generate-trip">
            <FormattedMessage
              id="mbuLE+De1P"
              defaultMessage="Start planning - It's free"
              description="Button text for starting trip planning"
            />
          </TextButton>
          {!props.loggedIn && (
            <BodyText variant="medium" weight="bold">
              <FormattedMessage
                id="JWEedessdS"
                defaultMessage="Already have trips? <link>Log in</link>"
                description="Text for users who already have trips with login link"
                values={{
                  link: (chunks: React.ReactNode) => <TextLink key="login-link" onClick={onLoginClick}>{chunks}</TextLink>,
                }}
              />
            </BodyText>
          )}
        </Group>
      </Group>
      <Sheet padding="20 16" tabletPadding={24} background="neutral-eight" borderRadius="L" paletteType="default">
        <Group direction="vertical" gap={4}>
          <BodyText variant="medium" weight="bold" colour="highlight-secondary">
            <FormattedMessage
              id="6r9C9Xnzye"
              defaultMessage="You've been invited to try our"
              description="Text for invitation to try new feature"
            />
          </BodyText>
          <Group direction="horizontal" gap={12}>
            <Heading variant="heading3" colour="neutral-one">
              <FormattedMessage
                id="/lVpvIxVpi"
                defaultMessage="New AI TripPlanner"
                description="Heading for new AI TripPlanner feature"
              />
            </Heading>
            <StatusLabel variant="confirmation">
              <FormattedMessage
                id="s+BLJEDYp4"
                defaultMessage="Beta"
                description="Beta label for new feature"
              />
            </StatusLabel>
          </Group>
          <Group direction="vertical" gap={8}>
            <BodyText variant="medium">
              <FormattedMessage
                id="bHcmadA5Ot"
                defaultMessage="Chat with your personal travel designer who will help{br}craft the perfect itinerary for you."
                description="Description of AI TripPlanner chat feature for crafting itineraries"
                values={{ br: <br /> }}
              />
            </BodyText>
            <TextButton kind="primary" size="large" to="/trip-planner/chat-trip-planner">
              <FormattedMessage
                id="V3UG3oegBt"
                defaultMessage="Give it a go"
                description="Button text for trying the chat trip planner"
              />
            </TextButton>
          </Group>
        </Group>
      </Sheet>
    </Group>
  )

  const contentWithoutFeatureFlag = (
    <Group direction="vertical" gap={32} desktopGap={40} desktopDirection="horizontal" desktopHorizontalAlign="space-between">
      <Group direction="vertical" gap={8}>
        <StatusLabel
          variant="confirmation"
          startIcon={<SolidSparklesIcon />}
        >
          <FormattedMessage
            id="AN+Ox9JWkN"
            defaultMessage="AI Trip Planner"
            description="Label for AI Trip Planner feature"
          />
        </StatusLabel>
        <HeadingTextBlock variant="heading1">
          <Heading variant="heading1">
            <FormattedMessage
              id="v5uvwpboew"
              defaultMessage="The <i>easiest</i> way"
              description="Heading text for easiest way to plan"
              values={{
                i: (chunks: React.ReactNode) => <i key="italic">{chunks}</i>,
              }}
            />
          </Heading>
          <Heading variant="heading1">
            <FormattedMessage
              id="bz1tHsQ/7B"
              defaultMessage="to plan a holiday, ever"
              description="Heading text for to plan a holiday ever"
            />
          </Heading>
        </HeadingTextBlock>
        <BodyText variant="large">
          <FormattedMessage
            id="MlXmgTxWfl"
            defaultMessage="We combine expert travel knowledge with powerful AI{br} to make trip planning effortless, smart and fun."
            description="Hero body copy describing AI-powered trip planning"
            values={{
              br: <CSSBreakpoint as="br" min="tablet" />,
            }}
          />
        </BodyText>
      </Group>
      <Group direction="vertical" gap={16} verticalAlign="center" horizontalAlign="start" desktopHorizontalAlign="center">
        <TextButton kind="primary" size="large" startIcon={<SolidSparklesIcon />} shape="round" to="/trip-planner/generate-trip">
          <FormattedMessage
            id="mbuLE+De1P"
            defaultMessage="Start planning - It's free"
            description="Button text for starting trip planning"
          />
        </TextButton>
        {!props.loggedIn && (
          <BodyText variant="medium" weight="bold">
            <FormattedMessage
              id="JWEedessdS"
              defaultMessage="Already have trips? <link>Log in</link>"
              description="Text for users who already have trips with login link"
              values={{
                link: (chunks: React.ReactNode) => <TextLink key="login-link" onClick={onLoginClick}>{chunks}</TextLink>,
              }}
            />
          </BodyText>
        )}
      </Group>
    </Group>
  )

  return (
    <Sheet
      paletteType="inverse"
      style={{ position: 'relative' }}
    >
      {showAITripPlanner ? contentWithFeatureFlag : contentWithoutFeatureFlag}
    </Sheet>
  )
}

export default TripLandingPageHeader
